import { useState } from "react";
import { TabListFieldsFragment } from "@src/generated/graphql";
import {
  highlightBracketedText,
  highlightUnderscoreText,
} from "src/utils/utils";
import DynamicMedia from "src/components/shared/DynamicMedia";

type SiteTabListProps = {
  content: TabListFieldsFragment;
};

const SiteTabList = ({ content }: SiteTabListProps) => {
  const { title, eyebrowText, tabListdescriptionJson, tabsCollection } =
    content;

  const descriptionText =
    tabListdescriptionJson?.json?.content?.[0]?.content?.[0]?.value ?? "";

  const isTabListEnterprise = content?.internalName === "tab_list_enterprise";

  // Video state management for each tab
  const [videoStates, setVideoStates] = useState<{
    [key: string]: {
      isPlaying: boolean;
      showVideo: boolean;
    };
  }>({});

  const togglePlayPause = (tabId: string, videoUrl: string) => {
    const isVideo = videoUrl && (videoUrl.includes('.mp4') || videoUrl.includes('.webm') || videoUrl.includes('.mov'));

    if (!isVideo) return;

    const currentState = videoStates[tabId];

    if (!currentState?.showVideo) {
      // First click - show video and start playing
      setVideoStates(prev => ({
        ...prev,
        [tabId]: {
          isPlaying: true,
          showVideo: true
        }
      }));

      // Small timeout to ensure video element is rendered before playing
      setTimeout(() => {
        const videoElement = document.querySelector(`[data-tab-id="${tabId}"] video`) as HTMLVideoElement;
        if (videoElement) {
          videoElement.play();
        }
      }, 10);
    } else {
      // Video is already showing - toggle play/pause
      const videoElement = document.querySelector(`[data-tab-id="${tabId}"] video`) as HTMLVideoElement;

      if (videoElement) {
        if (currentState?.isPlaying) {
          videoElement.pause();
          setVideoStates(prev => ({
            ...prev,
            [tabId]: {
              ...prev[tabId],
              isPlaying: false
            }
          }));
        } else {
          videoElement.play();
          setVideoStates(prev => ({
            ...prev,
            [tabId]: {
              ...prev[tabId],
              isPlaying: true
            }
          }));
        }
      }
    }
  };

  return (
    <div className="sitecore-tablist">
      <div className="container-fluid custom-spacer-col">
        {/* Header Section */}
        <div className="sitecore-tablist-header">
          <span
            className="highlight-bracket-text mx-w-770 d-block text-center mx-auto sitecore-tablist-title"
            dangerouslySetInnerHTML={{
              __html: highlightBracketedText(title ?? "", "bold"),
            }}
          />
          <p className="marketing-platform__subtitle">{eyebrowText}</p>
          {descriptionText && (
            <p
              className="marketing-platform__description text-center mx-w-770 mx-auto"
              dangerouslySetInnerHTML={{
                __html: highlightUnderscoreText((descriptionText ?? "")
                  .replace(/\\n/g, "<br/><br/>")
                  .replace(/\n/g, "<br/><br/>")
                ),
              }}
            />
          )}
        </div>

        {/* Content Section */}
        <div className="sitecore-tablist__columns">
          {tabsCollection?.items.map((tab, index) => {
            if (!tab) return null;

            const tabId = tab.sys.id || `tab-${index}`;
            const tabTitle = tab.tabTitle;
            const description =
              tab.tabDescription?.json?.content?.[0]?.content?.[0]?.value;
            const subDescription =
              tab.tabSubDescription?.json?.content?.[0]?.content?.[0]?.value;
            const image = tab?.tabForegroundImage?.url;
            const bgImage = tab?.tabBackgroundImage?.url;


            const isVideo = image && (image.includes('.mp4') || image.includes('.webm') || image.includes('.mov'));
            const currentVideoState = videoStates[tabId];

            // Use bgImage as thumbnail initially, then switch to video (image) when playing
            const mediaSrc = isVideo && !currentVideoState?.showVideo ? bgImage : image;

            return (
              <div
                key={tabId}
                className="sitecore-tablist__column"
              >
                <div
                  className="marketing-platform__content-image"
                  data-tab-id={tabId}
                  style={{
                    position: "relative",
                    ...(image?.endsWith(".json")
                      ? {
                        height:
                          "clamp(400px, calc(400px + (100vw - 500px) * 0.2), 600px)",
                      }
                      : {})
                  }}
                >
                  <DynamicMedia
                    src={mediaSrc ?? ""}
                    alt={tab?.tabForegroundImage?.title ?? ""}
                    ariaLabel={tab?.tabForegroundImage?.title ?? ""}
                    lottieClassName="lottie"
                    videoAutoplay={false}
                    lottieProps={{
                      loop: true,
                      autoplay: false,
                    }}
                  />

                  {/* Play/Pause Button for Videos */}
                  {isVideo && (
                    <button
                      onClick={() => togglePlayPause(tabId, image)}
                      className="video-detail-play-pause-btn"
                    >
                      {currentVideoState?.showVideo && currentVideoState?.isPlaying ? (
                        // Pause icon - when video is playing
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 1V11"
                            stroke="black"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M9 1V11"
                            stroke="black"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      ) : (
                        // Play icon - when showing thumbnail or video is paused
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M3 1L10 6L3 11V1Z" fill="black" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>

                <div className="marketing-platform__content-info">
                  {isTabListEnterprise && (
                    <p
                      className="marketing-platform__content-eyebrow"
                      dangerouslySetInnerHTML={{
                        __html: highlightUnderscoreText(tab.tabTitle ?? ""),
                      }}
                    />
                  )}

                  {tabTitle && (
                    <h3 className="blog-heading-3 text-center">
                      {tabTitle}
                    </h3>
                  )}

                  {description && (
                    <p className="marketing-platform__content-title">
                      {description}
                    </p>
                  )}

                  {subDescription && (
                    <p
                      className={`marketing-platform__content-description ${isTabListEnterprise ? "light" : ""}`}
                      dangerouslySetInnerHTML={{
                        __html: highlightUnderscoreText(subDescription ?? ""),
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SiteTabList;